import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/********************************************************************************
 * Copyright (c) 2019 TypeFox and others
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License v. 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 ********************************************************************************/
import { useContext, useRef, useState } from 'react';
import { Autocomplete, Box, Button, Checkbox, FormControlLabel, FormHelperText, TextField } from '@mui/material';
import { GenerateTokenDialog } from '../../../components/generate-token-dialog';
import { isError } from '../../../extension-registry-types';
import { MainContext } from '../../../context';
import { useUserNamespaces } from '../namespaces/use-user-namespaces';
import { useNamespaceExtensions } from './use-namespace-extensions';
export const GenerateAccessTokenDialog = props => {
    const context = useContext(MainContext);
    const { data: namespaces = [], isLoading } = useUserNamespaces();
    const abortController = useRef(new AbortController());
    const [open, setOpen] = useState(false);
    const [namespace, setNamespace] = useState('');
    const [extension, setExtension] = useState('');
    const [namespaceInput, setNamespaceInput] = useState('');
    const [extensionInput, setExtensionInput] = useState('');
    const { data: extensions = [], isLoading: extensionsLoading } = useNamespaceExtensions(namespace);
    const [publishingOnly, setPublishingOnly] = useState(false);
    // typed text that is not a picked option would otherwise be silently dropped from the scope
    const namespaceInvalid = namespaceInput !== namespace;
    const extensionInvalid = extensionInput !== extension;
    const handleGenerate = async (description) => {
        if (!context.user) {
            throw new Error('Not logged in');
        }
        const token = await context.service.createAccessToken(abortController.current, context.user, description, {
            namespace: namespace || undefined,
            extension: extension || undefined,
            publishingOnly
        });
        if (isError(token)) {
            throw token;
        }
        props.handleTokenGenerated();
        return token.value;
    };
    return (_jsxs(_Fragment, { children: [_jsx(Button, { variant: 'outlined', onClick: () => setOpen(true), children: "Generate new token" }), _jsx(GenerateTokenDialog, { open: open, onClose: () => {
                    setOpen(false);
                    setNamespace('');
                    setExtension('');
                    setNamespaceInput('');
                    setExtensionInput('');
                    setPublishingOnly(false);
                }, canGenerate: !namespaceInvalid && !extensionInvalid, onGenerate: handleGenerate, onError: context.handleError, children: _jsxs(Box, { mt: 2, display: 'flex', flexDirection: 'column', gap: 2, children: [_jsx(Autocomplete, { fullWidth: true, options: namespaces.map(ns => ns.name), loading: isLoading, clearOnBlur: false, value: namespace || null, inputValue: namespaceInput, onInputChange: (_, value) => setNamespaceInput(value), onChange: (_, value) => {
                                setNamespace(value ?? '');
                                // the extension belongs to the namespace, so a stale one must not outlive it
                                setExtension('');
                                setExtensionInput('');
                            }, renderInput: params => (_jsx(TextField, { ...params, label: 'Namespace (optional)', error: namespaceInvalid, helperText: namespaceInvalid
                                    ? 'Select a namespace from the list or clear the field.'
                                    : 'Restrict the token to this namespace. Leave empty for a token that works everywhere you can publish.' })) }), _jsx(Autocomplete, { fullWidth: true, options: extensions, loading: extensionsLoading, disabled: !namespace, clearOnBlur: false, value: extension || null, inputValue: extensionInput, onInputChange: (_, value) => setExtensionInput(value), onChange: (_, value) => setExtension(value ?? ''), renderInput: params => (_jsx(TextField, { ...params, label: 'Extension (optional)', error: extensionInvalid, helperText: extensionInvalid
                                    ? 'Select an extension from the list or clear the field.'
                                    : 'Restrict the token to one extension of the namespace.' })) }, namespace), _jsxs(Box, { children: [_jsx(FormControlLabel, { label: 'Publishing only', control: _jsx(Checkbox, { checked: publishingOnly, onChange: e => setPublishingOnly(e.target.checked) }) }), _jsx(FormHelperText, { children: "The token can only publish extensions. It cannot create namespaces, delete extensions or use administration endpoints." })] })] }) })] }));
};
//# sourceMappingURL=generate-access-token-dialog.js.map